import * as React from 'react';
import { useEffect, useState } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, collectionGroup, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserProfile, UserStatus, useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'motion/react';
import { Users, Check, X, Trash2, Mail, Clock, ShieldAlert, Loader2, AlertCircle, FileText, Filter } from 'lucide-react';
import { FunnelIntelligenceTab } from './FunnelIntelligenceTab';

interface VerdictRecord {

  ticker: string;
  analyzedAt: string;
  verdict: string;
  archetype: string;
  confidenceScore: number;
  oracleScore: number;
  intrinsicValue: number;
  currentPrice: number;
  marginOfSafety: number;
  uid?: string;
}

export const AdminDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'verdicts' | 'funnelIntelligence'>('users');

  // ── Users state ──────────────────────────────────────────────────────────
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Verdicts state ───────────────────────────────────────────────────────
  const [verdicts, setVerdicts] = useState<VerdictRecord[]>([]);
  const [verdictsLoading, setVerdictsLoading] = useState(false);
  const [verdictsError, setVerdictsError] = useState<string | null>(null);

  // ── Users: real-time listener ────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map(d => d.data() as UserProfile);
      setUsers(userList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setUsersLoading(false);
    }, (err) => {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load users. Please check your permissions.');
      setUsersLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ── Verdicts: fetch on tab switch ────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'verdicts') return;
    setVerdictsLoading(true);
    setVerdictsError(null);

    const fetchVerdicts = async () => {
      try {
        const q = query(
          collectionGroup(db, 'analyses'),
          orderBy('analyzedAt', 'desc'),
          limit(200)
        );
        const snapshot = await getDocs(q);

        // Build a uid → email map from already-loaded users
        const uidToEmail: Record<string, string> = {};
        users.forEach(u => { if (u.uid) uidToEmail[u.uid] = u.email; });

        const records: VerdictRecord[] = snapshot.docs.map(d => {
          const data = d.data();
          // Path: verdictLog/{uid}/analyses/{analysisId}
          const uid = d.ref.parent.parent?.id ?? '';
          return {
            ticker: data.ticker ?? '—',
            analyzedAt: data.analyzedAt ?? '',
            verdict: data.verdict ?? '—',
            archetype: data.archetype ?? '—',
            confidenceScore: data.confidenceScore ?? 0,
            oracleScore: data.oracleScore ?? 0,
            intrinsicValue: data.intrinsicValue ?? 0,
            currentPrice: data.currentPrice ?? 0,
            marginOfSafety: data.marginOfSafety ?? 0,
            uid,
            email: uidToEmail[uid] ?? uid,
          } as VerdictRecord & { email: string };
        });

        setVerdicts(records);
      } catch (err: any) {
        console.error('Verdict log fetch error:', err);
        setVerdictsError('Failed to load verdict log. You may need to create a Firestore index on analyzedAt for the analyses collection group.');
      } finally {
        setVerdictsLoading(false);
      }
    };

    fetchVerdicts();
  }, [activeTab, users]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleStatusChange = async (uid: string, status: UserStatus) => {
    setUpdatingId(uid);
    setError(null);
    try {
      await updateDoc(doc(db, 'users', uid), { status });
    } catch (err: any) {
      setError(`Failed to update status: ${err.message || 'Unknown error'}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (uid: string) => {
    if (uid === currentUser?.uid) {
      setError('You cannot delete your own account from the dashboard.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    setUpdatingId(uid);
    setError(null);
    try {
      await deleteDoc(doc(db, 'users', uid));
    } catch (err: any) {
      setError(`Failed to delete user: ${err.message || 'Unknown error'}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // ── Verdict helpers ───────────────────────────────────────────────────────
  const verdictColor = (verdict: string) => {
    const v = verdict.toUpperCase();
    if (v === 'FAT PITCH' || v === 'BUY') return 'text-success-700 bg-success-50 border-success-100';
    if (v === 'ACCUMULATE' || v === 'CORE HOLD') return 'text-success-700 bg-success-50 border-success-100';
    if (v === 'PASS') return 'text-ink-600 bg-surface-2 border-ink-200';
    if (v === 'AVOID') return 'text-danger-700 bg-danger-50 border-danger-100';
    return 'text-ink-600 bg-surface-2 border-ink-200';
  };

  const mosColor = (mos: number) => {
    if (mos >= 25) return 'text-success-700';
    if (mos >= 10) return 'text-warning-700';
    if (mos >= 0) return 'text-ink-600';
    return 'text-danger-700';
  };

  // ── Loading state (users only — verdicts have their own) ──────────────────
  if (usersLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-oxblood"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif font-medium italic text-ink flex items-center gap-3">
            {activeTab === 'users'
              ? <><Users className="w-8 h-8 text-oxblood" /> User Management</>
              : activeTab === 'verdicts'
              ? <><FileText className="w-8 h-8 text-oxblood" /> Verdict Log</>
              : <><Filter className="w-8 h-8 text-oxblood" /> Funnel Intelligence</>
            }
          </h1>
          <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mt-2">
            {activeTab === 'users'
              ? 'Approve or deny access requests to Oracle Insight'
              : activeTab === 'verdicts'
              ? 'All analyses across all users — most recent first'
              : 'Log of automated funnel screens and pre-inflection scans'
            }
          </p>
        </div>
        <div className="bg-oxblood px-4 py-2 rounded-md">
          <span className="text-paper text-[10px] font-medium uppercase tracking-widest">
            {activeTab === 'users' ? `${users.length} Total Users` : activeTab === 'verdicts' ? `${verdicts.length} Analyses` : 'Admin View'}
          </span>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-2 mb-6 p-1 bg-surface-2 rounded-md w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
            activeTab === 'users' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          Users
        </button>
        <button
          onClick={() => setActiveTab('verdicts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
            activeTab === 'verdicts' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          Verdict Log
        </button>
        <button
          onClick={() => setActiveTab('funnelIntelligence')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
            activeTab === 'funnelIntelligence' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Funnel Intelligence
        </button>
      </div>

      {/* Error banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-danger-50 border border-danger-100 rounded-md flex items-center gap-3 text-danger-600"
          >
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-danger-600 hover:text-danger-700">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── USERS TAB ────────────────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="grid gap-4">
          {users.map((user) => (
            <motion.div
              key={user.uid}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="oi-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:border-oxblood/30 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                  user.role === 'admin' ? 'bg-oxblood-50 text-oxblood' : 'bg-surface-2 text-ink-600'
                }`}>
                  {user.role === 'admin' ? <ShieldAlert className="w-6 h-6" /> : <Users className="w-6 h-6" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif font-medium italic text-ink">{user.displayName || 'Unnamed User'}</h3>
                    {user.role === 'admin' && (
                      <span className="px-2 py-0.5 bg-oxblood-50 text-oxblood text-[10px] font-medium uppercase tracking-wider rounded-md border border-oxblood/20">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 mt-1">
                    <div className="flex items-center gap-2 text-sm text-ink-500">
                      <Mail className="w-3.5 h-3.5" />
                      {user.email}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-ink-500">
                      <Clock className="w-3.5 h-3.5" />
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 mr-4">
                  <span className={`px-3 py-1 rounded-md text-xs font-medium uppercase tracking-wider border ${
                    user.status === 'approved' ? 'bg-success-50 text-success-700 border-success-100' :
                    user.status === 'denied'   ? 'bg-danger-50 text-danger-700 border-danger-100' :
                                                 'bg-warning-50 text-warning-700 border-warning-100'
                  }`}>
                    {user.status}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {updatingId === user.uid ? (
                    <Loader2 className="w-5 h-5 text-ink animate-spin" />
                  ) : (
                    <>
                      {user.status !== 'approved' && (
                        <button onClick={() => handleStatusChange(user.uid, 'approved')}
                          className="p-2 bg-success-50 text-success-600 hover:bg-success-100 rounded-md transition-all" title="Approve">
                          <Check className="w-5 h-5" />
                        </button>
                      )}
                      {user.status !== 'denied' && (
                        <button onClick={() => handleStatusChange(user.uid, 'denied')}
                          className="p-2 bg-danger-50 text-danger-600 hover:bg-danger-100 rounded-md transition-all" title="Deny">
                          <X className="w-5 h-5" />
                        </button>
                      )}
                      {user.uid !== currentUser?.uid && (
                        <button onClick={() => handleDelete(user.uid)}
                          className="p-2 bg-surface-2 text-ink-500 hover:bg-surface hover:text-danger-600 rounded-md transition-all" title="Delete">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {users.length === 0 && (
            <div className="text-center py-20 bg-surface rounded-lg border-2 border-dashed border-ink-200">
              <Users className="w-12 h-12 text-ink-300 mx-auto mb-4" />
              <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500">No users found in the system.</p>
            </div>
          )}
        </div>
      )}

      {/* ── VERDICT LOG TAB ──────────────────────────────────────────────── */}
      {activeTab === 'verdicts' && (
        <div>
          {verdictsLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-oxblood"></div>
            </div>
          )}

          {verdictsError && (
            <div className="p-4 bg-danger-50 border border-danger-100 rounded-md flex items-start gap-3 text-danger-600 mb-4">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{verdictsError}</span>
            </div>
          )}

          {!verdictsLoading && !verdictsError && verdicts.length === 0 && (
            <div className="text-center py-20 bg-surface rounded-lg border-2 border-dashed border-ink-200">
              <FileText className="w-12 h-12 text-ink-300 mx-auto mb-4" />
              <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500">No analyses logged yet.</p>
            </div>
          )}

          {!verdictsLoading && verdicts.length > 0 && (
            <div className="oi-card overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[80px_1fr_120px_90px_90px_90px_120px] gap-4 px-6 py-3 bg-surface-2 border-b border-ink-200">
                {['Ticker', 'User', 'Verdict', 'IV', 'Price', 'MoS', 'Analyzed'].map(h => (
                  <div key={h} className="text-[9px] font-medium uppercase tracking-widest text-ink-500">{h}</div>
                ))}
              </div>

              {/* Table rows */}
              {verdicts.map((v, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.015 }}
                  className="grid grid-cols-[80px_1fr_120px_90px_90px_90px_120px] gap-4 px-6 py-4 border-b border-ink-200 last:border-0 hover:bg-surface-2 transition-colors items-center"
                >
                  <div className="font-mono font-medium text-sm text-ink">{v.ticker}</div>
                  <div className="text-xs text-ink-500 truncate">{(v as any).email || v.uid}</div>
                  <div>
                    <span className={`px-2 py-0.5 rounded-md text-[9px] font-medium uppercase tracking-widest border ${verdictColor(v.verdict)}`}>
                      {v.verdict}
                    </span>
                  </div>
                  <div className="text-sm font-mono text-ink">
                    {v.intrinsicValue > 0 ? `$${v.intrinsicValue.toFixed(2)}` : '—'}
                  </div>
                  <div className="text-sm font-mono text-ink-600">
                    {v.currentPrice > 0 ? `$${v.currentPrice.toFixed(2)}` : '—'}
                  </div>
                  <div className={`text-sm font-mono font-medium ${mosColor(v.marginOfSafety)}`}>
                    {v.marginOfSafety > 0 ? '+' : ''}{v.marginOfSafety.toFixed(1)}%
                  </div>
                  <div className="text-[10px] text-ink-500">
                    {v.analyzedAt ? new Date(v.analyzedAt).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : '—'}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* ── FUNNEL INTELLIGENCE TAB ────────────────────────────────────────── */}
      {activeTab === 'funnelIntelligence' && (
        <FunnelIntelligenceTab />
      )}
    </div>
  );
};
